import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import {
  Add,
  Edit,
  Delete,
  DragIndicator,
  FormatListBulleted,
} from "@mui/icons-material";
import { DndContext, closestCenter } from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { BrandButton } from "components/brand";
import ItemsSelectModal from "components/ItemsSelectModal";
import {
  getSubcategoryById,
  createSubcategory,
  updateSubcategory,
  deleteSubcategory,
  reorderSubcategories,
  updateCategoryItems,
  getAllProductsWithoutPagination,
  getAllMenuCategories,
} from "actions";
import { ADMIN_TEXTS } from "constants/adminTexts";
import { NOTIFY } from "constants/notifyType";
import {
  SPINNER_SIZE,
  EMPTY_MODAL,
  EMPTY_DELETE_DIALOG,
  ICON_GRAY,
} from "constants/general";
import Notify from "helpers/Notify";
import { useDndSensors } from "hooks/useDndSensors";
import "./SubcategoryManager.scss";

const EMPTY_ITEMS_MODAL = {
  open: false,
  subcategoryId: null,
  fetchedItems: [],
  fetchLoading: false,
};

const SubcategoryItem = ({
  subcategory,
  onEdit,
  onDelete,
  onItems,
  itemsLoading,
}) => {
  const {
    setNodeRef,
    transform,
    transition,
    isDragging,
    attributes,
    listeners,
  } = useSortable({ id: subcategory.id });

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`subcategory-item${isDragging ? " subcategory-item--dragging" : ""}`}
      {...attributes}
    >
      <Box {...listeners} className="subcategory-item__handle">
        <DragIndicator />
      </Box>
      <Typography className="subcategory-item__name">
        {subcategory.name}
      </Typography>
      <Box className="subcategory-item__actions">
        <Tooltip title={ADMIN_TEXTS.menuSubcategories.manageItems}>
          <IconButton
            size="small"
            onClick={() => onItems(subcategory.id)}
            className="items-button"
            disabled={itemsLoading}
          >
            {itemsLoading ? (
              <CircularProgress size={16} />
            ) : (
              <FormatListBulleted htmlColor={ICON_GRAY} />
            )}
          </IconButton>
        </Tooltip>
        <IconButton
          size="small"
          onClick={() => onEdit(subcategory)}
          className="edit-button"
        >
          <Edit color="action" />
        </IconButton>
        <IconButton
          size="small"
          onClick={() => onDelete(subcategory.id, subcategory.name)}
          className="delete-button"
        >
          <Delete color="action" />
        </IconButton>
      </Box>
    </div>
  );
};

export const SubcategoryManager = ({ categoryId, initialSubcategories }) => {
  const dispatch = useDispatch();
  const { loading, reorderLoading } = useSelector(
    (state) => state.menuSubcategories,
  );
  const categories = useSelector((state) => state.menuCategories.categories);

  useEffect(() => {
    if (!categories.length) dispatch(getAllMenuCategories());
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [subcategories, setSubcategories] = useState(
    initialSubcategories || [],
  );
  const [modal, setModal] = useState(EMPTY_MODAL);
  const [deleteDialog, setDeleteDialog] = useState(EMPTY_DELETE_DIALOG);
  const [itemsModal, setItemsModal] = useState(EMPTY_ITEMS_MODAL);
  const [itemsSaving, setItemsSaving] = useState(false);

  const sensors = useDndSensors();

  useEffect(() => {
    setSubcategories(initialSubcategories || []);
  }, [initialSubcategories]);

  const openAddModal = () =>
    setModal({ open: true, mode: "add", id: null, name: "" });
  const openEditModal = (sub) =>
    setModal({
      open: true,
      mode: "edit",
      id: sub.id,
      name: sub.name,
      categoryId: sub.categoryId ?? categoryId,
      nameTouched: false,
    });
  const closeModal = () => setModal(EMPTY_MODAL);

  const handleModalNameChange = (e) =>
    setModal((prev) => ({ ...prev, name: e.target.value, nameTouched: true }));

  const handleModalSubmit = async () => {
    const name = modal.name.trim();
    if (!name) return;
    try {
      if (modal.mode === "add") {
        const result = await dispatch(
          createSubcategory({ name, categoryId }),
        ).unwrap();
        setSubcategories((prev) => [...prev, result]);
      } else {
        const result = await dispatch(
          updateSubcategory({
            id: modal.id,
            name,
            categoryId: modal.categoryId,
          }),
        ).unwrap();
        // If the category changed, remove it from this list — it now belongs elsewhere
        if (modal.categoryId !== categoryId) {
          setSubcategories((prev) => prev.filter((s) => s.id !== modal.id));
        } else {
          setSubcategories((prev) =>
            prev.map((s) => (s.id === result.id ? result : s)),
          );
        }
      }
      closeModal();
      dispatch(getAllMenuCategories());
    } catch {
      // error handled in thunk via Notify
    }
  };

  const handleDeleteClick = (id, name) =>
    setDeleteDialog({ open: true, id, name });

  const closeDeleteDialog = () => setDeleteDialog(EMPTY_DELETE_DIALOG);

  const handleDeleteConfirm = async () => {
    const { id } = deleteDialog;
    closeDeleteDialog();
    try {
      await dispatch(deleteSubcategory(id)).unwrap();
      setSubcategories((prev) => prev.filter((s) => s.id !== id));
      dispatch(getAllMenuCategories());
    } catch {
      // error handled in thunk via Notify
    }
  };

  const handleDragEnd = async ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const prev = subcategories;
    const oldIndex = prev.findIndex((s) => s.id === active.id);
    const newIndex = prev.findIndex((s) => s.id === over.id);
    const reordered = arrayMove(prev, oldIndex, newIndex);
    setSubcategories(reordered);
    try {
      await dispatch(
        reorderSubcategories({
          categoryId,
          subcategoryIds: reordered.map((s) => s.id),
        }),
      ).unwrap();
      dispatch(getAllMenuCategories());
    } catch {
      setSubcategories(prev);
    }
  };

  const openItemsModal = async (subcategoryId) => {
    setItemsModal({
      open: false,
      subcategoryId,
      fetchedItems: [],
      fetchLoading: true,
    });
    try {
      const result = await dispatch(getSubcategoryById(subcategoryId)).unwrap();
      setItemsModal({
        open: true,
        subcategoryId,
        fetchedItems: result.items || [],
        fetchLoading: false,
      });
    } catch {
      setItemsModal(EMPTY_ITEMS_MODAL);
    }
  };
  const closeItemsModal = () => setItemsModal(EMPTY_ITEMS_MODAL);

  const activeSubcategoryItemIds = itemsModal.fetchedItems.map((i) => i.id);

  const handleSubcategoryItemsSave = async (selectedProducts) => {
    const { subcategoryId, fetchedItems } = itemsModal;
    const originalIds = new Set(fetchedItems.map((i) => i.id));
    const selectedIdSet = new Set(selectedProducts.map((p) => p.id));

    const addedIds = selectedProducts
      .filter((p) => !originalIds.has(p.id))
      .map((p) => p.id);
    const removedIds = fetchedItems
      .filter((i) => !selectedIdSet.has(i.id))
      .map((i) => i.id);

    if (addedIds.length === 0 && removedIds.length === 0) {
      closeItemsModal();
      return;
    }

    setItemsSaving(true);
    try {
      const ops = [];
      if (addedIds.length > 0) {
        ops.push(
          dispatch(
            updateCategoryItems({
              productIds: addedIds,
              categoryId,
              subcategoryId,
            }),
          ).unwrap(),
        );
      }
      if (removedIds.length > 0) {
        ops.push(
          dispatch(
            updateCategoryItems({
              productIds: removedIds,
              subcategoryId: null,
            }),
          ).unwrap(),
        );
      }
      await Promise.all(ops);
      setSubcategories((prev) =>
        prev.map((s) =>
          s.id === subcategoryId ? { ...s, items: selectedProducts } : s,
        ),
      );
      Notify(NOTIFY.type.success, NOTIFY.message.update);
      closeItemsModal();
      setItemsSaving(false);

      const allProducts = await dispatch(
        getAllProductsWithoutPagination(),
      ).unwrap();
      setSubcategories((prev) =>
        prev.map((s) => ({
          ...s,
          items: allProducts.filter((p) => p.subcategory?.id === s.id),
        })),
      );
    } catch {
      setItemsSaving(false);
    }
  };

  return (
    <Box className="subcategory-manager">
      <Box className="subcategory-manager__header">
        <Typography variant="h6">
          {ADMIN_TEXTS.menuSubcategories.sectionTitle}
        </Typography>
        <IconButton onClick={openAddModal} size="small" color="primary">
          <Add />
        </IconButton>
      </Box>

      <Box className="subcategory-manager__list">
        {subcategories.length === 0 ? (
          <Typography
            variant="body2"
            color="text.secondary"
            className="subcategory-manager__empty"
          >
            {ADMIN_TEXTS.menuSubcategories.noSubcategories}
          </Typography>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={subcategories.map((s) => s.id)}
              strategy={verticalListSortingStrategy}
            >
              {subcategories.map((sub) => (
                <SubcategoryItem
                  key={sub.id}
                  subcategory={sub}
                  onEdit={openEditModal}
                  onDelete={handleDeleteClick}
                  onItems={openItemsModal}
                  itemsLoading={
                    itemsModal.fetchLoading &&
                    itemsModal.subcategoryId === sub.id
                  }
                />
              ))}
            </SortableContext>
          </DndContext>
        )}
        {reorderLoading && (
          <Typography
            variant="caption"
            color="text.secondary"
            className="subcategory-manager__reorder-hint"
          >
            {ADMIN_TEXTS.menuSubcategories.reordering}
          </Typography>
        )}
      </Box>

      <Dialog open={modal.open} onClose={closeModal} maxWidth="xs" fullWidth>
        <DialogTitle>
          {modal.mode === "add"
            ? ADMIN_TEXTS.menuSubcategories.addSubcategory
            : ADMIN_TEXTS.menuSubcategories.editSubcategory}
        </DialogTitle>
        <DialogContent>
          <Box className="subcategory-manager__modal-field">
            <TextField
              autoFocus
              fullWidth
              label={ADMIN_TEXTS.menuSubcategories.nameLabel}
              value={modal.name}
              onChange={handleModalNameChange}
              onKeyDown={(e) => e.key === "Enter" && handleModalSubmit()}
              placeholder={ADMIN_TEXTS.menuSubcategories.enterName}
              error={modal.nameTouched && !modal.name.trim()}
              helperText={
                modal.nameTouched && !modal.name.trim()
                  ? ADMIN_TEXTS.menuSubcategories.nameRequired
                  : ""
              }
            />
            {modal.mode === "edit" && (
              <FormControl fullWidth>
                <InputLabel>
                  {ADMIN_TEXTS.menuSubcategories.categoryLabel}
                </InputLabel>
                <Select
                  value={modal.categoryId ?? ""}
                  label={ADMIN_TEXTS.menuSubcategories.categoryLabel}
                  onChange={(e) =>
                    setModal((prev) => ({
                      ...prev,
                      categoryId: e.target.value,
                    }))
                  }
                >
                  {categories.map((cat) => (
                    <MenuItem key={cat.id} value={cat.id}>
                      {cat.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <BrandButton
            variant="secondary"
            onClick={closeModal}
            disabled={loading}
          >
            {ADMIN_TEXTS.common.cancel}
          </BrandButton>
          <BrandButton
            variant="primary"
            onClick={handleModalSubmit}
            disabled={loading || !modal.name.trim()}
          >
            {loading ? (
              <CircularProgress size={SPINNER_SIZE} color="inherit" />
            ) : modal.mode === "add" ? (
              ADMIN_TEXTS.menuSubcategories.createSubcategory
            ) : (
              ADMIN_TEXTS.menuSubcategories.updateSubcategory
            )}
          </BrandButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteDialog.open}
        onClose={closeDeleteDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {ADMIN_TEXTS.menuSubcategories.deleteSubcategory}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {ADMIN_TEXTS.menuSubcategories.deleteConfirm} "{deleteDialog.name}"?{" "}
            {ADMIN_TEXTS.menuSubcategories.deleteConfirmMessage}
          </Typography>
        </DialogContent>
        <DialogActions>
          <BrandButton variant="secondary" onClick={closeDeleteDialog}>
            {ADMIN_TEXTS.common.cancel}
          </BrandButton>
          <BrandButton variant="error" onClick={handleDeleteConfirm}>
            {ADMIN_TEXTS.common.delete}
          </BrandButton>
        </DialogActions>
      </Dialog>

      <ItemsSelectModal
        open={itemsModal.open}
        onClose={closeItemsModal}
        onSave={handleSubcategoryItemsSave}
        selectedIds={activeSubcategoryItemIds}
        saving={itemsSaving || itemsModal.fetchLoading}
        title={ADMIN_TEXTS.menuSubcategories.itemsModalTitle}
      />
    </Box>
  );
};
